import type { DefaultRootState } from "react-redux";
import { createSelector } from "reselect";
import {
  getActions,
  getAllJSCollectionActions,
} from "ee/selectors/entitiesSelector";
import { get } from "lodash";
import type { FunctionCallingConfigFormToolField } from "components/formControls/FunctionCallingConfigControl/types";

const getActionSchemaGeneratingState = (state: DefaultRootState) =>
  state.ui.pluginActionEditor.isSchemaGenerating;

export const getIsAgentSchemaDirty = (state: DefaultRootState, id: string) =>
  !!state.ui.actionSchema.isAgentSchemaDirty[id];

export const isActionSchemaGenerating = (id: string) =>
  createSelector(
    [getActionSchemaGeneratingState],
    (isSchemaGeneratingMap) =>
      id in isSchemaGeneratingMap && isSchemaGeneratingMap[id],
  );

export const isJSActionGeneratingSchema = (
  state: DefaultRootState,
  collectionId: string,
) => state.ui.jsPane.isSchemaGenerating[collectionId];

export const getIsAnyActionSchemaDirty = (actionId: string) =>
  createSelector(
    getActions,
    getAllJSCollectionActions,
    (state: DefaultRootState) => getIsAgentSchemaDirty(state, actionId),
    (actions, jsCollectionActions, isAgentSchemaDirty) => {
      const agentQuery = actions.find(
        (action) => action.config.id === actionId,
      );

      const isAgentQueryDirty = get(
        agentQuery,
        "config.isDirtyMap.TOOL_CALL_REFINEMENT",
        false,
      );

      const allAgentChatQueryFunctions: FunctionCallingConfigFormToolField[] =
        get(
          agentQuery,
          "config.actionConfiguration.formData.aiChatAssistant.input.functions",
          [],
        );

      const isAnyFunctionDirty = allAgentChatQueryFunctions.some((tool) => {
        if (tool.entityType === "Query") {
          const action = actions.find(
            (action) => action.config.id === tool.entityId,
          );

          if (action) {
            return action.config.isDirtyMap?.SCHEMA_GENERATION;
          }
        } else if (tool.entityType === "JSFunction") {
          const jsFunction = jsCollectionActions.find(
            (jsFunction) => jsFunction.id === tool.entityId,
          );

          if (jsFunction) {
            return jsFunction.isDirtyMap?.SCHEMA_GENERATION;
          }
        }

        return false;
      });

      return isAgentQueryDirty || isAnyFunctionDirty || isAgentSchemaDirty;
    },
  );

export const getIsWarningPublishAnvilApplicationOpen = (
  state: DefaultRootState,
) => state.ui.actionSchema.warnDirtyBeforePublish;

export const getIsSavingSchemaPostWarning = (state: DefaultRootState) =>
  state.ui.actionSchema.isSavingSchemaPostWarning;
